/**
 * Procedural Web Audio: engine hum tied to speed, pitch-varied pickup chimes,
 * crash burst, UI blips, overdrive whoosh. No external assets (generator APIs
 * unavailable); everything is synthesized. Unlocks on first user gesture.
 */
export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private muted = false;
  private master: GainNode | null = null;
  private engineOsc: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private duck = 1;

  constructor() {
    const unlock = () => {
      void this.unlock();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    this.context = new AudioContextClass();
    await this.context.resume();
    this.master = this.context.createGain();
    this.master.gain.value = this.muted ? 0 : 0.9;
    this.master.connect(this.context.destination);
    this.unlocked = true;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(muted ? 0 : 0.9, this.context.currentTime, 0.05);
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Duck music/engine while hitstop holds so impacts read. */
  setDuck(value: number): void {
    this.duck = value;
  }

  startEngine(): void {
    if (!this.context || !this.master || this.engineOsc) return;
    this.engineOsc = this.context.createOscillator();
    this.engineOsc.type = 'sawtooth';
    this.engineOsc.frequency.value = 48;
    this.engineFilter = this.context.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 220;
    this.engineFilter.Q.value = 4;
    this.engineGain = this.context.createGain();
    this.engineGain.gain.value = 0;
    this.engineOsc.connect(this.engineFilter).connect(this.engineGain).connect(this.master);
    this.engineOsc.start();
  }

  stopEngine(): void {
    if (!this.context || !this.engineGain) return;
    this.engineGain.gain.setTargetAtTime(0, this.context.currentTime, 0.1);
    const osc = this.engineOsc;
    setTimeout(() => osc?.stop(), 400);
    this.engineOsc = null;
  }

  updateEngine(speedRatio: number, overdrive: boolean): void {
    if (!this.context || !this.engineOsc || !this.engineGain || !this.engineFilter) return;
    const now = this.context.currentTime;
    const target = (0.028 + speedRatio * 0.05) * this.duck * (overdrive ? 1.3 : 1);
    this.engineGain.gain.setTargetAtTime(target, now, 0.08);
    this.engineOsc.frequency.setTargetAtTime(42 + speedRatio * 90 + (overdrive ? 24 : 0), now, 0.09);
    this.engineFilter.frequency.setTargetAtTime(180 + speedRatio * 900, now, 0.09);
  }

  /** Pitch-varied chime; rng keeps variance deterministic. */
  cell(rng: () => number, isSuper = false): void {
    const blip = this.tone('triangle', isSuper ? 520 : 420, isSuper ? 1040 : 840, 0.16, 0.09);
    if (blip) blip.playbackRateDetune(rng);
  }

  nearMiss(): void {
    this.tone('square', 180, 300, 0.07, 0.04);
  }

  overdrive(): void {
    this.tone('sawtooth', 120, 620, 0.5, 0.1);
  }

  crash(): void {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime;
    // Filtered noise burst.
    const length = this.context.sampleRate * 0.4;
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    let seedState = 987654321;
    for (let i = 0; i < length; i += 1) {
      // Tiny inline LCG: deterministic noise texture, no Math.random.
      seedState = (seedState * 1664525 + 1013904223) >>> 0;
      data[i] = ((seedState / 4294967296) * 2 - 1) * (1 - i / length);
    }
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    const filter = this.context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2400, now);
    filter.frequency.exponentialRampToValueAtTime(120, now + 0.38);
    const gain = this.context.createGain();
    gain.gain.setValueAtTime(0.5, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
    source.connect(filter).connect(gain).connect(this.master);
    source.start(now);

    this.tone('sine', 220, 40, 0.5, 0.3);
  }

  uiClick(): void {
    this.tone('triangle', 660, 880, 0.06, 0.05);
  }

  uiBack(): void {
    this.tone('triangle', 440, 320, 0.08, 0.05);
  }

  private tone(
    type: OscillatorType,
    from: number,
    to: number,
    duration: number,
    gainValue: number,
  ): { playbackRateDetune(rng: () => number): void } | null {
    if (!this.context || !this.master || this.context.state !== 'running') return null;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const now = this.context.currentTime;
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(to, 1), now + duration * 0.8);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(gainValue * this.duck, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.05);
    return {
      playbackRateDetune(rng: () => number) {
        oscillator.detune.value = (rng() - 0.5) * 120;
      },
    };
  }

  dispose(): void {
    this.stopEngine();
    void this.context?.close();
    this.context = null;
  }
}
